import { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { Notifications } from '@mantine/notifications';
import {
  Box,
  Container,
  Grid,
  Title,
  Text,
  TextInput,
  Group,
  MultiSelect,
  Badge,
  ActionIcon,
  Drawer,
  Button,
  Stack,
  Divider,
  MantineProvider,
  Loader,
  Tabs,
  Skeleton,
  ScrollArea,
  Paper,
} from '@mantine/core';
import { Head, router } from '@inertiajs/react';
import { IconSearch, IconAdjustmentsHorizontal, IconCalendarUp, IconCalendarDown } from '@tabler/icons-react';
import EventCard from '../../components/Cards/EventCard';
import AdCard from '../../components/Ads/AdCard';
import BannerAd from '../../components/Ads/BannerAd';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import EventDrawerContent from '../../components/Drawer/EventDrawerContent';
import { StablePagination } from '../../components/StablePagination';
import { useMediaQuery } from '@mantine/hooks';
import '../../styles/EventIndex.css';

interface DanceStyle {
  id: number;
  name: string;
}

interface EventType {
  id: number;
  name: string;
}

interface Event {
  id: number;
  name: string;
  slug: string;
  description?: string;
  location?: string;
  dance_style_ids?: number[];
  dance_styles?: { id: number; name: string }[];
  event_type_ids?: number[];
  event_types?: { id: number; name: string }[];
  event_start_date?: string;
  event_end_date?: string;
  venue_name?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  image_url?: string;
}

interface BannerAd {
  id: number;
  title: string;
  description?: string;
  url: string;
  banner_url?: string;
  ad_type: string;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total_pages: number;
}

interface EventIndexProps {
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
  upcoming_events: Event[];
  past_events: Event[];
  dance_styles: DanceStyle[];
  event_types: EventType[];
  upcoming_total_count: number;
  past_total_count: number;
  upcoming_pagination: PaginationInfo;
  past_pagination: PaginationInfo;
  filters?: any;
  active_tab?: string;
  banner_ads?: BannerAd[];
}

export default function EventIndex({
  upcoming_events = [],
  past_events = [],
  dance_styles,
  event_types,
  upcoming_total_count = 0,
  past_total_count = 0,
  upcoming_pagination = { current_page: 1, per_page: 20, total_pages: 1 },
  past_pagination = { current_page: 1, per_page: 20, total_pages: 1 },
  filters = {},
  page_meta,
  active_tab = 'upcoming',
  banner_ads = [],
}: EventIndexProps) {
  // --- Tab state ---
  const [activeTab, setActiveTab] = useState<string | null>(active_tab);

  // --- Filter state ---
  const [searchName, setSearchName] = useState(filters.name || '');
  const [geoLocation, setGeoLocation] = useState(filters.location || '');
  const [selectedDanceStyles, setSelectedDanceStyles] = useState<string[]>(
    Array.isArray(filters.dance_style_ids) ? filters.dance_style_ids.map(String) : [],
  );
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(
    Array.isArray(filters.event_type_ids) ? filters.event_type_ids.map(String) : [],
  );
  const [selectedMonths, setSelectedMonths] = useState<string[]>(
    Array.isArray(filters.month) ? filters.month.map(String) : [],
  );
  const [selectedYears, setSelectedYears] = useState<string[]>(
    Array.isArray(filters.year) ? filters.year.map(String) : [],
  );

  const isMobile = useMediaQuery('(max-width: 576px)');

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [debouncingSearch, setDebouncingSearch] = useState(false);

  // Drawer + pagination + details
  const [filterDrawerOpened, setFilterDrawerOpened] = useState(false);
  const [upcomingCurrentPage, setUpcomingCurrentPage] = useState(upcoming_pagination.current_page);
  const [pastCurrentPage, setPastCurrentPage] = useState(past_pagination.current_page);
  const [detailDrawerOpened, setDetailDrawerOpened] = useState(false);
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [drawerContentType, setDrawerContentType] = useState<'event' | 'artist'>('event');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Build options
  const danceStyleOptions = dance_styles ? dance_styles.map((s) => ({ value: s.id.toString(), label: s.name })) : [];

  const eventTypeOptions = event_types ? event_types.map((t) => ({ value: t.id.toString(), label: t.name })) : [];

  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const monthOptions = monthNames.map((m, i) => ({
    value: (i + 1).toString(),
    label: m,
  }));

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    { length: 5 }, // Show 5 years
    (_, i) => currentYear - 2 + i,
  ).map((y) => ({
    value: y.toString(),
    label: y.toString(),
  }));

  // Count active filters
  const activeFilterCount =
    (searchName ? 1 : 0) +
    (geoLocation ? 1 : 0) +
    selectedDanceStyles.length +
    selectedEventTypes.length +
    selectedMonths.length +
    selectedYears.length;

  // Helper function to get current events and pagination based on active tab
  const getCurrentEvents = () => {
    if (activeTab === 'past') {
      return {
        events: past_events,
        totalCount: past_total_count,
        currentPage: pastCurrentPage,
        pagination: past_pagination,
        setCurrentPage: setPastCurrentPage,
      };
    }

    // Default to upcoming
    return {
      events: upcoming_events,
      totalCount: upcoming_total_count,
      currentPage: upcomingCurrentPage,
      pagination: upcoming_pagination,
      setCurrentPage: setUpcomingCurrentPage,
    };
  };

  const handleAdClick = async (ad: BannerAd) => {
    try {
      // Track the click
      await fetch(`/admin/ads/${ad.id}/track_click`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
        },
        credentials: 'same-origin',
      });

      // Open the ad URL
      window.open(ad.url, '_blank');
    } catch (error) {
      console.error('Failed to track ad click:', error);
      // Still open the URL even if tracking fails
      window.open(ad.url, '_blank');
    }
  };

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }
    // console.log('🤓 Event Index Page meta:', page_meta);
    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  useEffect(() => {
    console.log('🚀 upcoming_events:', upcoming_events);
    console.log('🚀 past_events:', past_events);
  }, [upcoming_events, past_events]);

  // Fetch data with current filters and pagination
  const fetchData = (upcomingPage = upcomingCurrentPage, pastPage = pastCurrentPage) => {
    setIsLoading(true);

    // Build filter object - only include non-empty values
    const filterParams: Record<string, any> = {};

    if (searchName) filterParams.name = searchName;
    if (geoLocation) filterParams.location = geoLocation;
    if (selectedDanceStyles.length) filterParams.dance_style_ids = selectedDanceStyles;
    if (selectedEventTypes.length) filterParams.event_type_ids = selectedEventTypes;
    if (selectedMonths.length) filterParams.month = selectedMonths;
    if (selectedYears.length) filterParams.year = selectedYears;

    // Use Inertia router to fetch data without full page reload
    router.visit(window.location.pathname, {
      data: {
        upcoming_page: upcomingPage,
        past_page: pastPage,
        active_tab: activeTab,
        per_page: upcoming_pagination.per_page, // Assuming same per_page for both tabs
        filter: Object.keys(filterParams).length > 0 ? filterParams : undefined,
      },
      preserveState: true,
      onFinish: () => {
        setIsLoading(false);
        setDebouncingSearch(false);
      },
    });
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    const { setCurrentPage } = getCurrentEvents();
    setCurrentPage(page);

    if (activeTab === 'upcoming') {
      fetchData(page, pastCurrentPage);
    } else {
      fetchData(upcomingCurrentPage, page);
    }
  };

  // Handle tab change
  const handleTabChange = (value: string | null) => {
    if (value) {
      setActiveTab(value);
    }
  };

  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setDetailDrawerOpened(true);
  };

  const renderDrawerContent = () => {
    if (drawerContentType === 'event' && selectedEvent) {
      return <EventDrawerContent event={selectedEvent} />;
    }
    return null;
  };

  // Debounce search input
  useEffect(() => {
    if (debouncingSearch) {
      const timer = setTimeout(() => {
        // Reset to page 1 for both tabs when filters change
        fetchData(1, 1);
        setUpcomingCurrentPage(1);
        setPastCurrentPage(1);
      }, 500); // 500ms debounce delay

      return () => clearTimeout(timer);
    }
  }, [
    debouncingSearch,
    searchName,
    geoLocation,
    selectedDanceStyles,
    selectedEventTypes,
    selectedMonths,
    selectedYears,
  ]);

  // Watch for filter changes and set debounce flag
  useEffect(() => {
    setDebouncingSearch(true);
  }, [searchName, geoLocation, selectedDanceStyles, selectedEventTypes, selectedMonths, selectedYears]);

  // Handlers
  const openDetail = (e: Event) => {
    setSelectedEvent(e);
    setDetailDrawerOpened(true);
  };

  const clearFilters = () => {
    setSearchName('');
    setGeoLocation('');
    setSelectedDanceStyles([]);
    setSelectedEventTypes([]);
    setSelectedMonths([]);
    setSelectedYears([]);

    // Fetch data with cleared filters
    setTimeout(() => fetchData(1, 1), 0);
    setUpcomingCurrentPage(1);
    setPastCurrentPage(1);
  };

  const applyFilters = () => {
    fetchData(1, 1);
    setUpcomingCurrentPage(1);
    setPastCurrentPage(1);
    setFilterDrawerOpened(false);
  };

  // Get current tab's events and pagination
  const { events, totalCount, currentPage, pagination } = getCurrentEvents();

  return (
    <>
      <MantineProvider>
        <Notifications position="top-right" zIndex={2077} />
        {page_meta && (
          <Helmet>
            <title>{page_meta.title}</title>
            <meta name="description" content={page_meta.description} />

            {/* Open Graph tags */}
            <meta property="og:title" content={page_meta.title} />
            <meta property="og:description" content={page_meta.description} />
            <meta property="og:type" content="website" />
            <meta property="og:url" content={page_meta.url} />
            <meta property="og:image" content={page_meta.image} />

            {/* Twitter Card tags */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={page_meta.title} />
            <meta name="twitter:description" content={page_meta.description} />
            <meta name="twitter:image" content={page_meta.image} />
          </Helmet>
        )}
        <Head title="Events" />

        <Container size="md" mt="xl" mb="8rem">
          {/* Search + filter button */}
          <Box
            className="filter-wrapper"
            style={{
              position: 'sticky',
              top: '8px',
              zIndex: 100,
              backgroundColor: '#75757550',
              padding: '16px',
              marginBottom: '1.5rem',
              borderRadius: '12px',
              margin: '0px 8px 1.5rem 0px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(8px)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', width: '100%' }}>
              <TextInput
                leftSection={<IconSearch size={14} />}
                placeholder="Search by name or style..."
                value={searchName}
                onChange={(e) => setSearchName(e.currentTarget.value)}
                style={{ flex: 1, minWidth: 0 }}
              />

              <Box pos="relative" style={{ flexShrink: 0 }}>
                <ActionIcon variant="filled" size="xl" color="#EA580C" onClick={() => setFilterDrawerOpened(true)}>
                  <IconAdjustmentsHorizontal />
                </ActionIcon>
                {activeFilterCount > 0 && (
                  <Badge
                    color="orange"
                    size="lg"
                    variant="filled"
                    style={{
                      position: 'absolute',
                      top: -12,
                      right: -16,
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Box>
            </div>
          </Box>

          {/* Banner Ads - Between search and tabs */}
          {banner_ads && banner_ads.length > 0 && <BannerAd ads={banner_ads} />}

          {/* Tabs */}
          <Tabs value={activeTab} onChange={handleTabChange} mt="md" color="orange">
            <Tabs.List>
              <Tabs.Tab
                value="upcoming"
                style={{ color: '#FFF7ED', fontWeight: 'bold' }}
                leftSection={<IconCalendarUp size={20} stroke={1.5} color="#FFF7ED" />}
                rightSection={
                  <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
                    {upcoming_total_count}
                  </Badge>
                }
              >
                Upcoming Events
              </Tabs.Tab>

              <Tabs.Tab
                value="past"
                leftSection={<IconCalendarDown size={20} stroke={1.5} color="#FFF7ED" />}
                style={{ color: '#FFF7ED', fontWeight: 'bold' }}
                rightSection={
                  <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
                    {past_total_count}
                  </Badge>
                }
              >
                Past Events
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="upcoming" pt="md">
              {upcoming_events.length > 0 ? (
                <>
                  {/* Results info & pagination */}
                  <Group justify="flex-start" mb="md">
                    <Text className="text-white poppins">
                      Showing {upcoming_events.length} of {upcoming_total_count} upcoming events
                      {isLoading && <Loader color="orange" size="xs" ml="md" />}
                    </Text>
                  </Group>

                  {upcoming_pagination && upcoming_pagination.total_pages > 1 && (
                    <Group justify="flex-start" mb="md">
                      <StablePagination
                        total={upcoming_pagination.total_pages}
                        value={upcomingCurrentPage}
                        onChange={handlePageChange}
                        color="#EA580C"
                      />
                    </Group>
                  )}

                  {/* Events Grid */}
                  <Grid gutter="md">
                    {upcoming_events.map((e) => (
                      <Grid.Col key={e.id} span={{ base: 12, sm: 4, md: 4 }}>
                        <EventCard
                          event={{
                            ...e,
                            banner_url: e.image_url,
                          }}
                          onCardClick={() => handleEventClick(e)}
                        />
                      </Grid.Col>
                    ))}
                  </Grid>

                  {upcoming_pagination && upcoming_pagination.total_pages > 1 && (
                    <Group justify="flex-start" my="md">
                      <StablePagination
                        total={upcoming_pagination.total_pages}
                        value={upcomingCurrentPage}
                        onChange={handlePageChange}
                        color="#EA580C"
                      />
                    </Group>
                  )}
                </>
              ) : (
                !isLoading && (
                  <Paper p="lg" className="fade-in" radius="md" mb="xl" style={{ backgroundColor: '#8b8b8b50' }}>
                    <Stack gap="sm" align="flex-start">
                      <Text size="lg" fw={600} c="white">
                        No upcoming events found with these filters. Do you know of an event that should be here? Feel
                        free to list an event!
                      </Text>

                      <Text size="sm" c="white">
                        You don't need to be the event organizer to add events to support the local and global dance
                        community.
                      </Text>

                      <Button component="a" href="/suggest_event" color="#EA580C" radius="sm" size="md">
                        Add Event
                      </Button>
                    </Stack>
                  </Paper>
                )
              )}
            </Tabs.Panel>

            <Tabs.Panel value="past" pt="md">
              {past_events.length > 0 ? (
                <>
                  {/* Results info & pagination */}
                  <Group justify="flex-start" mb="md">
                    <Text className="text-white poppins">
                      Showing {past_events.length} of {past_total_count} past events
                      {isLoading && <Loader color="orange" size="xs" ml="md" />}
                    </Text>
                  </Group>

                  {past_pagination && past_pagination.total_pages > 1 && (
                    <Group justify="flex-start" mb="md">
                      <StablePagination
                        total={past_pagination.total_pages}
                        value={pastCurrentPage}
                        onChange={handlePageChange}
                        color="#EA580C"
                      />
                    </Group>
                  )}

                  {/* Events Grid */}
                  <Grid gutter="md">
                    {past_events.map((e) => (
                      <Grid.Col key={e.id} span={{ base: 12, sm: 4, md: 4 }}>
                        <EventCard
                          event={{
                            ...e,
                            banner_url: e.image_url,
                          }}
                          onCardClick={() => openDetail(e)}
                        />
                      </Grid.Col>
                    ))}
                  </Grid>

                  {past_pagination && past_pagination.total_pages > 1 && (
                    <Group justify="flex-start" my="md">
                      <StablePagination
                        total={past_pagination.total_pages}
                        value={pastCurrentPage}
                        onChange={handlePageChange}
                        color="#EA580C"
                      />
                    </Group>
                  )}
                </>
              ) : (
                !isLoading && (
                  <Paper p="lg" className="fade-in" radius="md" mb="xl" style={{ backgroundColor: '#8b8b8b50' }}>
                    <Stack gap="sm" align="flex-start">
                      <Text size="lg" fw={600} c="white">
                        No past events found. Help the community by adding event history!
                      </Text>

                      <Text size="sm" c="white">
                        You don't need to be the event organizer to add events to support the local and global dance
                        community.
                      </Text>

                      <Button component="a" href="/suggest_event" color="#EA580C" radius="sm" size="md">
                        Add Event
                      </Button>
                    </Stack>
                  </Paper>
                )
              )}
            </Tabs.Panel>
          </Tabs>
        </Container>

        {/* Filter Drawer */}
        <Drawer
          opened={filterDrawerOpened}
          onClose={() => setFilterDrawerOpened(false)}
          position="right"
          styles={{
            root: {
              zIndex: 1000,
              minWidth: '100px',
              width: isMobile ? '80vw' : '20vw',
            },
            header: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              borderBottom: '1px solid #333',
            },
            body: {
              backgroundColor: '#18181B',
              color: '#f2f2f2',
              minHeight: '100vh',
            },
            close: {
              color: '#f2f2f2',
              '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            },
          }}
        >
          <ScrollArea style={{ height: 'calc(100vh - 120px)' }}>
            <Stack p="md">
              <Text fw={600}>Filters</Text>
              <Divider />

              <TextInput
                label="Event name"
                placeholder="Any event"
                value={searchName}
                onChange={(e) => setSearchName(e.currentTarget.value)}
              />

              <TextInput
                label="Geolocation"
                disabled={true}
                placeholder="continent, country, state, or city"
                value={geoLocation}
                onChange={(e) => setGeoLocation(e.currentTarget.value)}
              />

              <MultiSelect
                label="Dance style"
                data={danceStyleOptions}
                value={selectedDanceStyles}
                onChange={setSelectedDanceStyles}
                placeholder="Any dance style"
                clearable
                searchable
              />

              <MultiSelect
                label="Event type"
                data={eventTypeOptions}
                value={selectedEventTypes}
                onChange={setSelectedEventTypes}
                placeholder="Any event type"
                clearable
                searchable
              />

              <MultiSelect
                label="Month"
                data={monthOptions}
                value={selectedMonths}
                onChange={setSelectedMonths}
                placeholder="Any month"
                clearable
              />

              <MultiSelect
                label="Year"
                data={yearOptions}
                value={selectedYears}
                onChange={setSelectedYears}
                placeholder="Any year"
                clearable
              />
            </Stack>
          </ScrollArea>

          <Group
            justify="apart"
            style={{
              position: 'sticky',
              bottom: 0,
              left: 0,
              right: 0,
              padding: '16px',
              background: '#18181B',
              borderTop: '1px solid #333',
              zIndex: 10,
            }}
          >
            <Button
              variant="light"
              color="orange"
              size="md"
              fullWidth
              className="clear-button"
              styles={{
                root: {
                  borderColor: '#EA580C10',
                  '&:disabled': {
                    borderColor: '#3F3F46',
                    color: '#71717A',
                  },
                },
                label: { fontSize: 12 },
              }}
              onClick={clearFilters}
              disabled={!activeFilterCount}
            >
              Clear all
            </Button>
            <Button
              variant="filled"
              fullWidth
              color="#EA580C"
              size="md"
              onClick={applyFilters}
              styles={() => ({ label: { fontSize: 12 } })}
            >
              Show {upcoming_total_count} Upcoming / {past_total_count} Past Events
              {isLoading && <Loader color="white" size="xs" ml="xs" />}
            </Button>
          </Group>
        </Drawer>

        {/* Detail Drawer */}
        {selectedEvent && (
          <DynamicDrawer
            opened={detailDrawerOpened}
            type="event"
            data={{
              ...selectedEvent,
              banner_url: selectedEvent.image_url,
            }}
            onClose={() => setDetailDrawerOpened(false)}
          />
        )}
      </MantineProvider>
    </>
  );
}
